import { isLiveUpcomingSlot } from '@atink/domain';
import { isMissedLinkClosed, linkSenderRole, missedLinkOwner } from '@/domain/missedLinks';
import { resolveDayOff } from '@/domain/artistAdmin';
import { gapKeyFor, turnedDownFor } from '@/domain/sweeperOutcomes';
import type {
  Artist,
  Booking,
  CustomerRecord,
  DayOfWeek,
  Deposit,
  Piece,
  RebookFollowupTask,
  HealingCheckTask,
  StudioId,
  SweeperOutcome,
  SweeperOutcomeKind,
  TopupPlan,
} from '@/domain/types';
import { recommendChannel, type ChannelRecommendation } from '@/domain/channelRecommendation';
import type { RedKind, SequenceStage, SequenceState } from '@/domain/sequenceFlow';
import { relativeFromIso } from '@/utils/format';
import { isDepositRedeemed } from '@/utils/bookingSummary';
import { piecePotFor, depositAmountLabel, forfeitedAud, isDepositForfeited } from '@/domain/depositStatus';

export type QueueBand = 'now' | 'today' | 'gap';

export type QueueKind =
  | 'deposit-on-file'
  | 'callback-firing'
  | 'callback-today'
  | 'pending-deposit'
  | 'missed-call'
  | 'unread-message'
  | 'pushed-lead'
  | 'missed-topup'
  | 'unfinished-piece'
  | 'rebook-followup'
  | 'healing-check'
  | 'gap-candidate';

/** Which lane of the operator queue a kind belongs to. See deriveCohort. */
export type QueueCohort = 'fresh' | 'money' | 'open-business';

/**
 * The five-rung fill ladder a gap-fill candidate sorts into, worked left to
 * right (board 08). Set on gap-candidate items by buildGapQueue; drives the
 * Sweeper's rung tabs. Rung 3 keeps the legacy value 'past-client' but is
 * broadened to open/unfinished business (incl. no-rebook mid-piece) and labelled
 * "Open business" in the UI.
 *   1 bring-forward · 2 deposit · 3 past-client (open business)
 *   4 expired (expired links & never-paid) · 5 no-show (forfeits & no-shows)
 */
export type GapBucket = 'bring-forward' | 'deposit' | 'past-client' | 'expired' | 'no-show';

export type QueueChannel = 'voice' | 'sms' | 'meta-dm' | 'ig-dm' | 'email';

/**
 * Where a pushed card came from (B8 / 75nf, diagram 05 "six feeders, one
 * worklist"): the card is LABELLED by its origin, whatever signal also fired.
 */
export type QueueOrigin = 'hot-lead' | 'date-taken' | 'self-book-expired' | 'web-enquiry';

export interface MatchEvidence {
  kind:
    | 'unfinished-piece'
    | 'style-match'
    | 'previous-booking'
    | 'spend-tier'
    | 'engagement'
    | 'future-signal'
    | 'mentioned-artist'
    | 'calendar-open'
    | 'calendar-busy'
    | 'proximity'
    | 'tier'
    | 'wait'
    | 'sequence'
    | 'deposit-age'
    | 'days-since-contact'
    | 'channel-choice';
  weight: number;
  text: string;
}

export interface QueueItem {
  id: string;
  kind: QueueKind;
  band: QueueBand;
  customer: CustomerRecord;
  reason: string;
  detail?: string;
  recommendedChannel: QueueChannel;
  channelReason: string;
  channelConfidence: ChannelRecommendation['confidence'];
  recommendedAt?: string;
  priority: number;
  matchScore?: number;
  matchReason?: string;
  evidence?: MatchEvidence[];
  depositId?: string;
  rebookFollowupTaskId?: string;
  /**
   * For a missed-topup item: the TopupPlan the Agent's 3-sequence chase acts on.
   * The card's chase-outcome buttons pass it straight to the topupChase* engine.
   */
  topupPlanId?: string;
  /** Which queue lane this item sorts into. Undefined for rail-only kinds. */
  cohort?: QueueCohort;
  /** A pushed card's origin, which its label shows (B8). */
  origin?: QueueOrigin;
  /** The customer also has an unanswered missed call (kept when another card wins the dedup). */
  missedCall?: boolean;
  /** Sequence-clock state for this customer, when one exists. */
  stage?: SequenceStage;
  redKind?: RedKind;
  /** True while the sequence's nextDueAt is still in the future. */
  dormant?: boolean;
  sequenceDueAtIso?: string;
  /** For gap-fill candidates: which rung of the five-rung ladder this sorts into. */
  gapBucket?: GapBucket;
  /**
   * For gap candidates on the deposit + open-business rungs only: days since the
   * client's most recent completed tattoo, when they have one. Drives the subtle
   * "Tattooed N days ago" flag (bd tv-25a). No cut-off — the day count is the
   * whole mechanism; the sweeper decides case by case.
   */
  recentlyTattooedDays?: number;
  /**
   * For bring-forward (rung 1) candidates: the client asked to be brought forward
   * if an earlier slot opens (`Booking.preferBroughtForward`). Always sorts to the
   * top of the rung automatically and drives the "brought forward on request"
   * badge on the candidate row (bd tv-dme, board 08).
   */
  preferBroughtForward?: boolean;
  /**
   * For bring-forward (rung 1) candidates: the ISO start of the future booking
   * that could jump the line. Lets the rung's sort control order the non-preferred
   * remainder by date (soonest vs furthest-out) without re-deriving it (tv-dme).
   */
  bringForwardBookingIso?: string;
  /**
   * For bring-forward (rung 1) candidates: does their booking fit the gap's
   * largest free block (Anas 1 Oct Q2)? The Sweeper hides the ones that don't
   * behind an "N don't fit" disclosure (B7).
   */
  fitsGap?: boolean;
  /**
   * For gap candidates: the Sweeper's latest call outcome on THIS gap that leaves
   * the client listed ("No answer" / "Call me back"), for the row's mark (B7).
   */
  sweeperOutcome?: SweeperOutcomeKind;
  /**
   * For pushed leads with a chosen callback time: the ISO instant. Its presence
   * both pins the card at its slot (via recommendedAt) and mirrors it onto the
   * Scheduled-calls rail.
   */
  pinnedCallbackAtIso?: string;
  /**
   * True for a pending-deposit item generated from a recently-expired,
   * non-superseded deposit link (see buildStandardQueue). Drives the "resend"
   * detail copy here and the resend action in QueueWorkModal.
   */
  depositExpired?: boolean;
  /**
   * The agent who booked/secured this item, when known (money cohort: from the
   * deposit's takenByAgentId). Display + attribution only; does not scope the
   * queue.
   */
  ownerAgentId?: string;
}

export interface GapFilter {
  artistId: string;
  dayIso: string;
}

interface BuildArgs {
  customers: Record<string, CustomerRecord>;
  bookings: Record<string, Booking>;
  deposits: Record<string, Deposit>;
  pieces: Record<string, Piece>;
  artists: Record<string, Artist>;
  /** Sales-team follow-up tasks from Outcome C — surfaced as a queue kind. */
  rebookFollowupTasks?: Record<string, RebookFollowupTask>;
  healingCheckTasks?: Record<string, HealingCheckTask>;
  /** Pay-over-time plans; a missed/overdue instalment feeds the Agent chase. */
  topupPlans?: Record<string, TopupPlan>;
  studioIds: StudioId[];
  nowIso: string;
  gapFilter?: GapFilter;
  /** Per-customer sequence-clock state, keyed by customer id. */
  sequenceState?: Record<string, SequenceState>;
  /** The Sweeper's call outcomes (B7): a turn-down takes the client off that gap. */
  sweeperOutcomes?: Record<string, SweeperOutcome>;
}

const HOUR = 60 * 60 * 1000;

export function buildQueue(args: BuildArgs): QueueItem[] {
  return args.gapFilter ? buildGapQueue(args, args.gapFilter) : buildStandardQueue(args);
}

function buildStandardQueue(args: BuildArgs): QueueItem[] {
  const allow = new Set(args.studioIds);
  const inStudio = (id: StudioId) => allow.size === 0 || allow.has(id);
  const now = new Date(args.nowIso).getTime();
  const items: QueueItem[] = [];

  for (const c of Object.values(args.customers)) {
    if (!inStudio(c.primaryStudioId)) continue;

    for (const sig of c.futureReadinessSignals) {
      if (sig.status !== 'scheduled') continue;
      const at = new Date(sig.scheduledFor).getTime();
      const diff = at - now;
      if (diff < -HOUR * 2 || diff > HOUR * 10) continue;
      // Pop into the top of the queue 15 minutes before the scheduled time.
      const firing = diff <= HOUR * 0.25;
      const sigChannel = castChannel(sig.recommendedChannel);
      items.push({
        id: `q-frs-${sig.id}`,
        kind: firing ? 'callback-firing' : 'callback-today',
        band: firing ? 'now' : 'today',
        customer: c,
        reason: firing ? 'Callback now' : 'Callback later today',
        detail: sig.rawPhrase,
        recommendedChannel: sigChannel,
        channelReason: `customer asked for ${labelChannel(sigChannel)} contact`,
        channelConfidence: 'high',
        recommendedAt: sig.scheduledFor,
        priority: firing ? 1000 - Math.abs(diff) / 60_000 : 700 - diff / 60_000,
        evidence: contextEvidence(c, [
          { kind: 'future-signal', weight: 25, text: `Captured phrase: "${sig.rawPhrase}"` },
        ]),
      });
    }
  }

  for (const d of Object.values(args.deposits)) {
    if (d.status !== 'paid' || d.bookingId) continue;
    // Used up on piece completion - nothing left on file to book (tv-2na.4).
    if (isDepositRedeemed(d)) continue;
    if (!inStudio(d.studioId)) continue;
    const c = args.customers[d.customerId];
    if (!c) continue;
    const paidMs = d.paidAt ? new Date(d.paidAt).getTime() : undefined;
    const paidAgoMs = paidMs != null ? now - paidMs : 0;
    const rec = recommendChannel(c);
    items.push({
      id: `q-depfile-${d.id}`,
      kind: 'deposit-on-file',
      band: 'today',
      customer: c,
      reason: 'Deposit on file, no date booked',
      detail: `${depositAmountLabel(d)} deposit paid ${formatHours(paidAgoMs)} ago, still no date booked.`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: d.paidAt,
      priority: 500,
      depositId: d.id,
      ...(d.takenByAgentId ? { ownerAgentId: d.takenByAgentId } : {}),
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 40,
          text: `Deposit on file: ${depositAmountLabel(d)} paid ${formatHours(paidAgoMs)} ago, no booking yet`,
        },
      ]),
    });
  }

  for (const d of Object.values(args.deposits)) {
    if (d.status !== 'pending') continue;
    if (!inStudio(d.studioId)) continue;
    const c = args.customers[d.customerId];
    if (!c) continue;
    const ms = new Date(d.expiresAt).getTime() - now;
    if (ms < 0 || ms > HOUR * 48) continue;
    const expiringSoon = ms <= HOUR * 4;
    const rec = recommendChannel(c);
    items.push({
      id: `q-dep-${d.id}`,
      kind: 'pending-deposit',
      band: expiringSoon ? 'now' : 'today',
      customer: c,
      reason: expiringSoon ? 'Deposit expires soon' : 'Deposit pending',
      detail: `$${d.amountAud} · expires in ${formatHours(ms)}`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: d.expiresAt,
      priority: expiringSoon ? 900 : 500,
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: expiringSoon ? 30 : 15,
          text: `$${d.amountAud} ${labelChannel(castChannel(d.channelSent))} link sent ${formatHours(now - new Date(d.createdAt).getTime())} ago, expires in ${formatHours(ms)}`,
        },
      ]),
    });
  }

  // Recently-expired, un-resent deposit links also surface under Awaiting
  // deposit (still `pending-deposit`, still `money` cohort) so the agent can
  // catch and resend them before the lead goes cold. Superseded (already
  // resent) or stale (>7d expired) links don't surface.
  for (const d of Object.values(args.deposits)) {
    if (d.status !== 'expired' || d.supersededByDepositId) continue;
    // D02-07: a slot lost to another client is the Agent's "Date taken" alert, not a resend.
    if (d.releasedReason === 'slot-taken') continue;
    if (!inStudio(d.studioId)) continue;
    // W5 (DECIDED 24 Sep): an unpaid link routes by WHO SENT IT — only an Agent's
    // (or customer self-book) link is the Agent's call-back. A Messenger link is
    // an Ops item; a Sweeper link sits on the Sweeper's missed-deposits list. A
    // closed-off / later-rebooked link is done.
    if (missedLinkOwner(linkSenderRole(d, args.bookings)) !== 'agent') continue;
    if (isMissedLinkClosed(d, args.bookings)) continue;
    const c = args.customers[d.customerId];
    if (!c) continue;
    const expiredAgoMs = now - new Date(d.expiresAt).getTime();
    if (expiredAgoMs < 0 || expiredAgoMs > 7 * 24 * HOUR) continue;
    const rec = recommendChannel(c);
    items.push({
      id: `q-dep-${d.id}`,
      kind: 'pending-deposit',
      band: 'today',
      customer: c,
      reason: 'Unpaid deposit link: call back',
      detail: `$${d.amountAud} · Deposit link expired — call back, resend`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: d.expiresAt,
      priority: 500,
      depositId: d.id,
      depositExpired: true,
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 15,
          text: `$${d.amountAud} deposit link expired ${formatHours(expiredAgoMs)} ago — resend`,
        },
      ]),
    });
  }

  for (const c of Object.values(args.customers)) {
    if (!inStudio(c.primaryStudioId)) continue;
    const lastVoiceIn = lastMatching(
      c.conversation,
      (e) => e.channel === 'voice' && e.direction === 'in',
    );
    if (lastVoiceIn) {
      const t = new Date(lastVoiceIn.timestamp).getTime();
      // 72h window so overnight/weekend voicemails persist into Monday's queue.
      if (now - t <= HOUR * 72) {
        const calledBack = c.conversation.some(
          (e) =>
            e.channel === 'voice' &&
            e.direction === 'out' &&
            new Date(e.timestamp).getTime() > t,
        );
        if (!calledBack) {
          items.push({
            id: `q-mc-${c.id}-${lastVoiceIn.id}`,
            kind: 'missed-call',
            band: 'today',
            customer: c,
            reason: 'Missed call',
            detail: lastVoiceIn.body.slice(0, 90),
            recommendedChannel: 'voice',
            channelReason: 'return their call',
            channelConfidence: 'high',
            recommendedAt: lastVoiceIn.timestamp,
            priority: 600 - (now - t) / 60_000,
            evidence: contextEvidence(c, [
              {
                kind: 'future-signal',
                weight: 18,
                text: `Voicemail: "${lastVoiceIn.body.slice(0, 120)}"`,
              },
            ]),
          });
        }
      }
    }

    const lastInboundMsg = lastMatching(
      c.conversation,
      (e) => e.direction === 'in' && e.channel !== 'voice',
    );
    // An IG/FB DM is the Messenger's until it is pushed (B8 / D03-02, diagram 03):
    // it never reaches the Agent as an unread card. A pushed lead has its own card.
    if (lastInboundMsg && !isMessengerOwnedDm(lastInboundMsg.channel)) {
      const t = new Date(lastInboundMsg.timestamp).getTime();
      if (now - t <= HOUR * 6) {
        const replied = c.conversation.some(
          (e) => e.direction === 'out' && e.authorRole !== 'system' && new Date(e.timestamp).getTime() > t,
        );
        if (!replied) {
          const inboundCh = castChannel(lastInboundMsg.channel);
          items.push({
            id: `q-msg-${lastInboundMsg.id}`,
            kind: 'unread-message',
            band: 'today',
            customer: c,
            reason: 'Unread message',
            detail: lastInboundMsg.body.slice(0, 90),
            recommendedChannel: inboundCh,
            channelReason: `they messaged you on ${labelChannel(inboundCh)}`,
            channelConfidence: 'high',
            recommendedAt: lastInboundMsg.timestamp,
            priority: 550 - (now - t) / 60_000,
            evidence: contextEvidence(c, [
              {
                kind: 'future-signal',
                weight: 15,
                text: `"${lastInboundMsg.body.slice(0, 120)}"`,
              },
            ]),
          });
        }
      }
    }
  }

  for (const p of Object.values(args.pieces)) {
    if (p.status !== 'in-progress' || p.nextPlannedSession) continue;
    const c = args.customers[p.customerId];
    if (!c || !inStudio(c.primaryStudioId)) continue;
    const rec = recommendChannel(c);
    const remaining = Math.max(0, p.estimatedTotalHours - p.hoursCompleted);
    items.push({
      id: `q-up-${p.id}`,
      kind: 'unfinished-piece',
      band: 'today',
      customer: c,
      reason: 'Unfinished piece',
      detail: `${p.designType} · ${p.placement} · ${p.hoursCompleted}/${p.estimatedTotalHours}h`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      priority: 300,
      evidence: contextEvidence(c, [
        {
          kind: 'unfinished-piece',
          weight: 20,
          text: `${p.title || p.designType.replace('-', ' ')} on ${p.placement} · ~${remaining}h left of ${p.estimatedTotalHours}h`,
        },
        ...(p.lastSessionDate
          ? [
              {
                kind: 'future-signal' as const,
                weight: 8,
                text: `Last session ${formatHours(now - new Date(p.lastSessionDate).getTime())} ago`,
              },
            ]
          : []),
      ]),
    });
  }

  // Sales-team rebook follow-ups (Outcome C). Surfaced as a queue item until
  // resolved. Higher priority while fresh (<48h), then decays.
  for (const task of Object.values(args.rebookFollowupTasks ?? {})) {
    if (task.resolvedAt) continue;
    if (!inStudio(task.studioId)) continue;
    const c = args.customers[task.customerId];
    if (!c) continue;
    const created = new Date(task.createdAt).getTime();
    const ageHrs = Math.max(0, (now - created) / HOUR);
    const rec = recommendChannel(c);
    const stale = ageHrs > 5 * 24;
    items.push({
      id: `q-rebookfu-${task.id}`,
      kind: 'rebook-followup',
      rebookFollowupTaskId: task.id,
      band: ageHrs < 48 ? 'today' : 'gap',
      customer: c,
      reason: 'Rebook follow-up',
      detail: task.note
        ? task.note.slice(0, 90)
        : `Floor team deferred to sales after ${args.artists[task.originalArtistId]?.firstName ?? 'their artist'}'s session.`,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: task.createdAt,
      priority: Math.max(380, 520 - ageHrs * 2) + (stale ? -30 : 0),
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 16,
          text: `Deferred from chair · ${args.artists[task.originalArtistId]?.firstName ?? 'artist'} · ${stale ? `stale ${Math.floor(ageHrs / 24)}d` : `${Math.floor(ageHrs)}h ago`}`,
        },
      ]),
    });
  }

  // Healing-check nudges (09 Jul). A completed session that didn't rebook in the
  // chair surfaces 7 days later ("how's it healing?" + line up the next piece).
  // Money cohort when a deposit still sits on the piece (set on the item below).
  for (const task of Object.values(args.healingCheckTasks ?? {})) {
    if (task.resolvedAt) continue;
    if (new Date(task.dueAt).getTime() > now) continue; // not due until T+7d
    if (!inStudio(task.studioId)) continue;
    const c = args.customers[task.customerId];
    if (!c) continue;
    const rec = recommendChannel(c);
    const daysAgo = Math.floor((now - new Date(task.createdAt).getTime()) / (24 * HOUR));
    items.push({
      id: `q-healing-${task.id}`,
      kind: 'healing-check',
      band: 'today',
      cohort: task.depositId ? 'money' : undefined,
      customer: c,
      reason: 'Healing check',
      detail: "How's the tattoo healing? Check in and line up the next piece.",
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt: task.dueAt,
      priority: 360,
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 14,
          text: `Session ${daysAgo}d ago · healing check due`,
        },
      ]),
    });
  }

  // Manually pushed leads (Prompt C). A sequence record with a sourceConversationId
  // is a lead a messager handed to the booking team. Mint a card for it even when the
  // customer has no other signal. B8: labelled by origin; a callback pinned at push
  // becomes tier 0 in its firing window (−15 min … +2 h), sits on the rail while it
  // is later, and is spent once the Agent has made contact at or after it. A lead
  // who books, pays or cancels leaves the sequences on the server (N7 / D05-28,
  // exitAgentSequenceTx); open tabs learn it from the sequenceState doorbell.
  for (const [customerId, seq] of Object.entries(args.sequenceState ?? {})) {
    if (!seq.sourceConversationId) continue;
    const c = args.customers[customerId];
    if (!c || !inStudio(c.primaryStudioId)) continue;
    // Expired-link resend routed to a taken date (Prompt I1): lands at the TOP of
    // the money band (priority above the 900 expiring-soon max), attributed to the
    // agent who took the deposit, carrying the deposit ref.
    const moneyResend = seq.cohort === 'money' && Boolean(seq.depositId);
    const rec = recommendChannel(c);
    const lastMsg = c.conversation[c.conversation.length - 1];
    const origin = pushOrigin(seq);
    const pin = livePin(seq, now);
    const resendDep = seq.depositId ? args.deposits[seq.depositId] : undefined;
    const evidence = contextEvidence(c, [
      {
        kind: 'future-signal',
        weight: 14,
        text: seq.description
          ? `Pushed by messaging - ${seq.description.slice(0, 100)}`
          : 'Pushed by messaging for a booking agent',
      },
    ]);
    if (pin === 'firing') {
      const at = new Date(seq.pinnedCallbackAt!).getTime();
      items.push({
        id: `q-push-cb-${c.id}`,
        kind: 'callback-firing',
        band: 'now',
        customer: c,
        origin,
        reason: 'Callback now',
        detail: seq.description ? seq.description.slice(0, 90) : undefined,
        recommendedChannel: c.phone ? 'voice' : rec.channel,
        channelReason: c.phone ? 'they asked for a call back at this time' : rec.reason,
        channelConfidence: 'high',
        recommendedAt: seq.pinnedCallbackAt,
        priority: 1000 - Math.abs(at - now) / 60_000,
        pinnedCallbackAtIso: seq.pinnedCallbackAt,
        evidence,
      });
      continue;
    }
    // FIFO position: when the work arrived (the last message), else now.
    const recommendedAt = lastMsg?.timestamp ?? args.nowIso;
    items.push({
      id: `q-push-${c.id}`,
      kind: 'pushed-lead',
      band: 'today',
      customer: c,
      origin,
      reason: moneyResend ? 'Deposit needs resend - date is filled' : ORIGIN_REASON[origin],
      detail: seq.description ? seq.description.slice(0, 90) : undefined,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      recommendedAt,
      priority: moneyResend ? 990 : 100,
      ...(moneyResend ? { cohort: 'money' as const, depositId: seq.depositId } : {}),
      ...(moneyResend && resendDep?.takenByAgentId
        ? { ownerAgentId: resendDep.takenByAgentId }
        : {}),
      evidence,
    });
  }

  // A callback pinned on ANY sequence record ("Call back at…" from the hang-up
  // sheet on a lead that was never pushed) fires the same way (B8 / D05-04).
  for (const [customerId, seq] of Object.entries(args.sequenceState ?? {})) {
    if (seq.sourceConversationId || !seq.pinnedCallbackAt) continue;
    const c = args.customers[customerId];
    if (!c || !inStudio(c.primaryStudioId)) continue;
    if (livePin(seq, now) !== 'firing') continue;
    const at = new Date(seq.pinnedCallbackAt).getTime();
    const rec = recommendChannel(c);
    items.push({
      id: `q-seq-cb-${c.id}`,
      kind: 'callback-firing',
      band: 'now',
      customer: c,
      reason: 'Callback now',
      recommendedChannel: c.phone ? 'voice' : rec.channel,
      channelReason: c.phone ? 'they asked for a call back at this time' : rec.reason,
      channelConfidence: 'high',
      recommendedAt: seq.pinnedCallbackAt,
      priority: 1000 - Math.abs(at - now) / 60_000,
      pinnedCallbackAtIso: seq.pinnedCallbackAt,
      evidence: contextEvidence(c, []),
    });
  }

  // Missed top-up chase (tv-9vr, Diagram 05). A pay-over-time plan with a missed
  // instalment feeds the Agent worklist as a fresh, oldest-first chase item: the
  // Agent runs the 3-sequence retention chase (prefer chasing to refilling)
  // rather than the Sweeper refilling the spot. The chase ENGINE already exists
  // (topupChaseFlow); this only surfaces the plan so its card can call it.
  for (const plan of Object.values(args.topupPlans ?? {})) {
    if (plan.status !== 'active') continue;
    if (!inStudio(plan.studioId)) continue;
    const c = args.customers[plan.customerId];
    if (!c) continue;
    const missed = plan.instalments.find((i) => i.status === 'missed');
    if (!missed) continue;
    const overdueMs = Math.max(0, now - new Date(missed.dueIso).getTime());
    const attempts = plan.chaseAttempts ?? 0;
    items.push({
      id: `q-topup-${plan.id}`,
      kind: 'missed-topup',
      band: 'today',
      customer: c,
      reason: 'Missed top-up, chase to retain',
      detail: `$${missed.amountAud} instalment missed ${formatHours(overdueMs)} ago${
        attempts > 0 ? ` · ${attempts}/3 sequences` : ''
      }.`,
      recommendedChannel: 'voice',
      channelReason: 'call to retain the booking before refilling the spot',
      channelConfidence: 'high',
      // Oldest-first: pin at the missed instalment's due date so the fresh tier
      // FIFO ordering surfaces the longest-overdue chase first.
      recommendedAt: missed.dueIso,
      priority: 640,
      topupPlanId: plan.id,
      evidence: contextEvidence(c, [
        {
          kind: 'future-signal',
          weight: 22,
          text: `Missed $${missed.amountAud} top-up ${formatHours(overdueMs)} ago · booking still held, chase to keep it`,
        },
      ]),
    });
  }

  // Stamp cohort and any sequence-clock state onto every item before ordering.
  for (const item of items) {
    // Respect a cohort the minter set explicitly (e.g. a healing-check with a
    // deposit still on the piece → money lane); otherwise derive from the kind.
    item.cohort = item.cohort ?? deriveCohort(item.kind);
    const seq = args.sequenceState?.[item.customer.id];
    if (seq) {
      item.stage = seq.stage;
      item.redKind = seq.redKind;
      item.sequenceDueAtIso = seq.nextDueAt;
      item.dormant = seq.nextDueAt != null && new Date(seq.nextDueAt).getTime() > now;
    }
    // Main-list items (those with a cohort) get position-justifying bullets so
    // their "Why this matters" expander explains where they sit and why. Append
    // to preserve the context evidence already stamped above.
    if (item.cohort) {
      item.evidence = [...(item.evidence ?? []), ...positionEvidence(item, args.nowIso)];
    }
  }

  const out = dedupPerCustomer(items);
  // A callback pinned for later rails at its time on whichever card represents
  // the customer (B8): it survives the dedup, and a lead with no other card
  // still gets its rail row.
  // A pin is the Agent's (sequence) callback, so it rails on the customer's
  // Agent-lane card when there is one (bgg4 #2: each lane now keeps its own).
  const byCustomer = new Map<string, number>();
  out.forEach((item, i) => {
    const at = byCustomer.get(item.customer.id);
    if (at === undefined || (laneOf(out[at]!.kind) !== 'agent' && laneOf(item.kind) === 'agent')) {
      byCustomer.set(item.customer.id, i);
    }
  });
  for (const [customerId, seq] of Object.entries(args.sequenceState ?? {})) {
    if (livePin(seq, now) !== 'later') continue;
    const i = byCustomer.get(customerId);
    if (i !== undefined) {
      out[i] = { ...out[i]!, pinnedCallbackAtIso: seq.pinnedCallbackAt };
      continue;
    }
    const c = args.customers[customerId];
    if (!c || !inStudio(c.primaryStudioId)) continue;
    const rec = recommendChannel(c);
    out.push({
      id: `q-seq-cb-${c.id}`,
      kind: 'callback-today',
      band: 'today',
      customer: c,
      reason: 'Callback later',
      recommendedChannel: c.phone ? 'voice' : rec.channel,
      channelReason: c.phone ? 'they asked for a call back at this time' : rec.reason,
      channelConfidence: 'high',
      recommendedAt: seq.pinnedCallbackAt,
      priority: 0,
      pinnedCallbackAtIso: seq.pinnedCallbackAt,
    });
  }
  return out.sort(compareByTier);
}

/**
 * One card per customer PER LANE (B8 / 75nf; bgg4 #2). The Agent and the
 * Sweeper each see at most one card for a customer, so a Sweeper-lane card (a
 * pending deposit at 500) can no longer swallow the same client's missed call
 * once it decays below 500 — the call stayed on no one's screen. Within a lane
 * the highest priority wins, EXCEPT that a
 * pushed card keeps its identity against the reactive signals (an unread
 * message, a missed call): the Agent sees a HOT LEAD in its arrival slot, not
 * "UNREAD". Whatever wins carries the missed-top-up plan (so the chase stays one
 * click away) and the missed-call flag (so the Alerts tab still lists it).
 */
function dedupPerCustomer(items: QueueItem[]): QueueItem[] {
  const byCustomer = new Map<string, QueueItem[]>();
  for (const item of items) {
    const key = `${laneOf(item.kind)}:${item.customer.id}`;
    byCustomer.set(key, [...(byCustomer.get(key) ?? []), item]);
  }
  const out: QueueItem[] = [];
  for (const group of byCustomer.values()) {
    let winner = group.reduce((a, b) => (b.priority > a.priority ? b : a));
    const pushed = group.find((i) => i.kind === 'pushed-lead');
    if (pushed && (winner.kind === 'unread-message' || winner.kind === 'missed-call')) winner = pushed;
    const topup = group.find((i) => i.topupPlanId);
    if (topup && !winner.topupPlanId) winner = { ...winner, topupPlanId: topup.topupPlanId };
    if (winner.kind !== 'missed-call' && group.some((i) => i.kind === 'missed-call')) winner = { ...winner, missedCall: true };
    out.push(winner);
  }
  return out;
}

const ORIGIN_REASON: Record<QueueOrigin, string> = {
  'hot-lead': 'Hot lead from Messenger',
  'date-taken': 'Held date was taken - re-call for a new date',
  'self-book-expired': 'Self-book link expired - follow up',
  'web-enquiry': 'Website form (email) - call back',
};

/** A pushed record's origin, from what pushed it (B8: label by origin). */
export function pushOrigin(seq: SequenceState): QueueOrigin {
  // D01-02: the website enquiry form pushes straight to the Agent (server-written).
  if (!seq.depositId && /^website enquiry\b/i.test(seq.description ?? '')) return 'web-enquiry';
  if (seq.depositId || /date (is|was) (taken|filled)|original date is taken/i.test(seq.description ?? '')) return 'date-taken';
  if (/self-book link expired/i.test(seq.description ?? '')) return 'self-book-expired';
  return 'hot-lead';
}

/** Minutes before a pinned callback it starts firing, and how long it keeps firing after. */
const PIN_LEAD_MS = 15 * 60 * 1000;
const PIN_TAIL_MS = 2 * HOUR;

/**
 * Where a callback pinned at push stands now (B8 / D05-04): 'firing' inside its
 * window (tier 0), 'later' before it (the rail), or null once it is spent — the
 * Agent made contact at or after the window opened, or the window closed.
 */
export function livePin(seq: SequenceState, nowMs: number): 'firing' | 'later' | null {
  if (!seq.pinnedCallbackAt) return null;
  const at = new Date(seq.pinnedCallbackAt).getTime();
  if (!Number.isFinite(at)) return null;
  // Spent by a contact made at/after the window opened AND after the pin was set
  // (the call that set "call back at…" is not the callback).
  if (seq.lastAttemptAt) {
    const contact = new Date(seq.lastAttemptAt).getTime();
    const pinnedAt = seq.pinnedAt ? new Date(seq.pinnedAt).getTime() : -Infinity;
    if (contact >= at - PIN_LEAD_MS && contact > pinnedAt) return null;
  }
  if (nowMs < at - PIN_LEAD_MS) return 'later';
  if (nowMs <= at + PIN_TAIL_MS) return 'firing';
  return null;
}

/** IG / FB DMs: the Messenger's channels (diagram 03). */
export function isMessengerOwnedDm(channel: string): boolean {
  return channel === 'ig-dm' || channel === 'meta-dm';
}

/**
 * Maps a queue kind to its cohort lane. Scheduled-call and gap kinds
 * (callback-firing, callback-today, gap-candidate) have no cohort and are
 * filtered out of the main list by consumers.
 */
export function deriveCohort(kind: QueueKind): QueueCohort | undefined {
  switch (kind) {
    case 'unread-message':
    case 'missed-call':
    case 'pushed-lead':
    case 'missed-topup':
      return 'fresh';
    case 'pending-deposit':
    case 'deposit-on-file':
      return 'money';
    case 'unfinished-piece':
    case 'rebook-followup':
    case 'healing-check':
      return 'open-business';
    case 'callback-firing':
    case 'callback-today':
    case 'gap-candidate':
      return undefined;
  }
}

/** Which role-based queue lane a kind belongs to. See laneOf. */
export type QueueLane = 'agent' | 'sweeper';

/**
 * Splits the shared queue by role (bd tv-570). The booking Agent works fresh
 * inquiries, scheduled callbacks and gap-fill; the Sweeper works the money and
 * open-business tail (deposits, unfinished pieces, rebooks, healing checks).
 * Pure classifier over the kind: the engine keeps building one full queue and
 * each screen filters to its own lane.
 */
export function laneOf(kind: QueueKind): QueueLane {
  switch (kind) {
    case 'unread-message':
    case 'missed-call':
    case 'pushed-lead':
    case 'missed-topup':
    case 'callback-firing':
    case 'callback-today':
    case 'gap-candidate':
      return 'agent';
    case 'deposit-on-file':
    case 'pending-deposit':
    case 'unfinished-piece':
    case 'rebook-followup':
    case 'healing-check':
      return 'sweeper';
  }
}

/**
 * The ordered channels whose action buttons a card should render (the card maps
 * 1:1 over this list). Always leads with the recommended channel, optionally
 * offers a voice dial when there's a phone, then at most one message channel the
 * customer is genuinely reachable on. Dark-red said-no items get no channel
 * buttons.
 */
export function queueActionsFor(item: QueueItem): QueueChannel[] {
  if (item.redKind === 'said-no') return [];

  const out: QueueChannel[] = [item.recommendedChannel];
  if (item.customer.phone && item.recommendedChannel !== 'voice') out.push('voice');
  const msg = bestMessageChannel(item.customer);
  if (msg && !out.includes(msg)) out.push(msg);
  return out;
}

// The single best message channel the customer is genuinely reachable on, in a
// fixed preference order. Reachability: sms needs a phone; meta-dm needs a prior
// Messenger conversation entry; ig-dm needs a handle; email needs an address.
function bestMessageChannel(customer: CustomerRecord): QueueChannel | undefined {
  const reachable: Record<Exclude<QueueChannel, 'voice'>, boolean> = {
    sms: Boolean(customer.phone),
    'meta-dm': customer.conversation.some((e) => e.channel === 'meta-dm'),
    'ig-dm': Boolean(customer.instagramHandle),
    email: Boolean(customer.email),
  };
  const order: Array<Exclude<QueueChannel, 'voice'>> = ['sms', 'meta-dm', 'ig-dm', 'email'];
  return order.find((ch) => reachable[ch]);
}

/**
 * One evidence bullet per real ranking signal behind an item's position, in the
 * shared { kind, weight, text } shape with modest POSITIVE weights so the card
 * renders them as "+N", not red. These are cosmetic labels, not scoring inputs.
 * The wait bullet's weight uses the passed nowIso (never Date.now()), while its
 * display text uses relativeFromIso, which reads the global sim clock.
 */
export function positionEvidence(item: QueueItem, nowIso: string): MatchEvidence[] {
  const out: MatchEvidence[] = [];

  if (item.cohort === 'fresh') {
    out.push({
      kind: 'tier',
      weight: 6,
      text: 'Fresh inquiry - worked before deposits and rebooks',
    });
  } else if (item.cohort === 'money') {
    out.push({
      kind: 'tier',
      weight: 6,
      text: 'Deposit received, no date locked in - after fresh, before rebooks',
    });
  } else if (item.cohort === 'open-business') {
    out.push({
      kind: 'tier',
      weight: 6,
      text: 'Open business - rebooks and unfinished work last',
    });
  }

  if (item.recommendedAt) {
    const ageMs = Math.max(0, new Date(nowIso).getTime() - new Date(item.recommendedAt).getTime());
    const waited = relativeFromIso(item.recommendedAt).replace(/ ago$/, '').replace(/^in /, '');
    out.push({
      kind: 'wait',
      weight: Math.min(12, 2 + Math.round(ageMs / (6 * HOUR))),
      text: `Waiting ${waited} - older items rise`,
    });
  }

  if (item.stage != null) {
    if (item.stage === 'red') {
      // Two flavours of red reach the list: never-reached (no answer after the
      // full attempt ladder) and said-no (declined, now on the nurture list).
      // They must read differently - said-no did answer.
      out.push({
        kind: 'sequence',
        weight: 4,
        text:
          item.redKind === 'said-no'
            ? 'Declined - handled by nurture'
            : 'No answer after 3 attempts',
      });
    } else {
      out.push({
        kind: 'sequence',
        weight: 4,
        text: `Sequence stage ${item.stage} - attempt ${item.stage}`,
      });
    }
  }

  out.push({
    kind: 'channel-choice',
    weight: 4,
    text: `${labelChannel(item.recommendedChannel)} chosen - ${item.channelReason}`,
  });

  return out;
}

// Tier 0 pops scheduled callbacks that are due now to the very top; then the
// three cohort lanes; rail-only kinds sink to the bottom (consumers filter them
// out of the main list, so their exact order does not matter).
function tierOf(item: QueueItem): number {
  if (item.kind === 'callback-firing') return 0;
  if (item.cohort === 'fresh') return 1;
  if (item.cohort === 'money') return 2;
  if (item.cohort === 'open-business') return 3;
  return 9;
}

// Missing recommendedAt sorts last. We use a large-but-FINITE sentinel (not
// Infinity) so two missing values subtract to 0 instead of NaN, keeping the
// fresh-tier comparator total and the sort stable for any future kind.
const NO_RECOMMENDED_AT = Number.MAX_SAFE_INTEGER;

function recommendedAtMs(item: QueueItem): number {
  return item.recommendedAt ? new Date(item.recommendedAt).getTime() : NO_RECOMMENDED_AT;
}

/**
 * Tier comparator for the standard queue. Dormant rows sink to the absolute
 * bottom across every tier (A9); among non-dormant rows, orders by tier, then:
 * fresh sorts FIFO oldest-first by recommendedAt ascending; every other tier
 * keeps highest-priority-first.
 */
export function compareByTier(a: QueueItem, b: QueueItem): number {
  // Dormant rows sink to the absolute bottom, across all tiers (A9).
  const da = a.dormant ? 1 : 0;
  const db = b.dormant ? 1 : 0;
  if (da !== db) return da - db;
  const ta = tierOf(a);
  const tb = tierOf(b);
  if (ta !== tb) return ta - tb;
  if (ta === 1) return recommendedAtMs(a) - recommendedAtMs(b);
  return b.priority - a.priority;
}

/**
 * Splits an already-ordered standard queue into the sections the operator
 * screen renders. Incoming order is preserved within each bucket (scheduledCalls
 * is additionally ordered oldest-first by recommendedAt for the rail).
 *
 * When `nowIso` is provided, the scheduled-calls rail is constrained to calls due
 * within the next 24h — callback-today and pinned-callback mirrors further out
 * (some are days away) are dropped from the rail. Omitting `nowIso` applies no
 * time filter, preserving existing callers.
 */
export function partitionQueue(
  items: QueueItem[],
  nowIso?: string,
): {
  scheduledDue: QueueItem[];
  main: QueueItem[];
  scheduledCalls: QueueItem[];
} {
  const scheduledDue: QueueItem[] = [];
  let scheduledCalls: QueueItem[] = [];
  const main: QueueItem[] = [];
  for (const item of items) {
    if (item.kind === 'callback-firing') scheduledDue.push(item);
    else if (item.kind === 'callback-today') scheduledCalls.push(item);
    else if (item.cohort) {
      main.push(item);
      // A pushed lead with a chosen callback is worked in place (main list, pinned
      // at its slot) AND mirrored onto the Scheduled-calls rail so the fixed time
      // point is visible there too.
      if (item.pinnedCallbackAtIso) scheduledCalls.push(item);
    }
  }
  // A pushed lead's rail row sits at its callback time, not its arrival slot.
  const railMs = (item: QueueItem) =>
    item.pinnedCallbackAtIso ? new Date(item.pinnedCallbackAtIso).getTime() : recommendedAtMs(item);
  scheduledCalls.sort((a, b) => railMs(a) - railMs(b));
  if (nowIso != null) {
    const cutoff = new Date(nowIso).getTime() + 24 * HOUR;
    scheduledCalls = scheduledCalls.filter((item) => railMs(item) <= cutoff);
  }
  return { scheduledDue, main, scheduledCalls };
}

const DAY_MS = 24 * HOUR;
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function gapShortDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTH_ABBR[d.getMonth()]}`;
}

/** Most recent conversation timestamp (ms), or null when never contacted. */
function lastContactMs(c: CustomerRecord): number | null {
  let latest: number | null = null;
  for (const entry of c.conversation) {
    const t = new Date(entry.timestamp).getTime();
    if (latest === null || t > latest) latest = t;
  }
  return latest;
}

/**
 * Baseline match signals shared by every gap bucket — the existing ranker
 * weights (previous bookings, unfinished piece, style, mention, spend,
 * future-readiness, engagement, proximity, calendar). Bucket-specific signals
 * (deposit age, days-since-contact, sequence stage) are layered on top.
 */
function gapBaseSignals(
  c: CustomerRecord,
  artist: Artist,
  args: BuildArgs,
  gapDayStart: number,
  custBookings: Booking[],
  custPieces: Piece[],
): { score: number; evidence: MatchEvidence[] } {
  const evidence: MatchEvidence[] = [];
  let score = 5;

  const prevWithArtist = custBookings.filter((b) => b.artistId === artist.id);
  if (prevWithArtist.length > 0) {
    const w = 35 + Math.min(20, prevWithArtist.length * 5);
    score += w;
    evidence.push({
      kind: 'previous-booking',
      weight: w,
      text: `Booked ${artist.displayName} ${prevWithArtist.length} time${prevWithArtist.length > 1 ? 's' : ''} before`,
    });
  }

  const unfinished = custPieces.find(
    (p) => p.assignedArtistId === artist.id && p.status === 'in-progress',
  );
  if (unfinished) {
    const remaining = Math.max(0, unfinished.estimatedTotalHours - unfinished.hoursCompleted);
    score += 45;
    evidence.push({
      kind: 'unfinished-piece',
      weight: 45,
      text: `Unfinished ${unfinished.designType}: ${unfinished.hoursCompleted}/${unfinished.estimatedTotalHours}h done, ~${remaining}h left`,
    });
  }

  if (c.designIntent?.type && artist.styleSpeciality.includes(c.designIntent.type)) {
    score += 22;
    evidence.push({
      kind: 'style-match',
      weight: 22,
      text: `${c.designIntent.type.replace('-', ' ')} matches ${artist.firstName}'s speciality`,
    });
  }

  const firstName = artist.firstName.toLowerCase();
  const handle = artist.instagramHandle.toLowerCase().replace('@', '');
  const mentionHit = c.conversation.find((m) => {
    const body = m.body.toLowerCase();
    return body.includes(firstName) || (handle && body.includes(handle));
  });
  if (mentionHit) {
    score += 30;
    evidence.push({ kind: 'mentioned-artist', weight: 30, text: `Mentioned ${artist.firstName} in a message` });
  }

  if (c.lifetimeSpendAud >= 5000) {
    score += 20;
    evidence.push({ kind: 'spend-tier', weight: 20, text: `VIP spend: $${c.lifetimeSpendAud.toLocaleString()} lifetime` });
  } else if (c.lifetimeSpendAud >= 2000) {
    score += 12;
    evidence.push({ kind: 'spend-tier', weight: 12, text: `High spend: $${c.lifetimeSpendAud.toLocaleString()} lifetime` });
  } else if (c.lifetimeSpendAud >= 500) {
    score += 6;
    evidence.push({ kind: 'spend-tier', weight: 6, text: `Returning customer: $${c.lifetimeSpendAud.toLocaleString()} lifetime` });
  }

  const scheduledSignal = c.futureReadinessSignals.find((s) => s.status === 'scheduled');
  if (scheduledSignal) {
    score += 18;
    evidence.push({ kind: 'future-signal', weight: 18, text: `Future-readiness: "${scheduledSignal.rawPhrase}"` });
  }

  if (c.engagementStatus === 'lapsed') {
    score += 8;
    evidence.push({ kind: 'engagement', weight: 8, text: 'Lapsed (not seen in months), reactivation lift' });
  } else if (c.engagementStatus === 'dormant') {
    score += 12;
    evidence.push({ kind: 'engagement', weight: 12, text: 'Dormant: long overdue, high re-engagement value' });
  }

  if (c.distanceToNearestStudioKm <= 5) {
    score += 6;
    evidence.push({ kind: 'proximity', weight: 6, text: `${c.distanceToNearestStudioKm.toFixed(1)} km from studio` });
  }

  const customerBookings = c.bookings
    .map((id) => args.bookings[id])
    .filter((b): b is NonNullable<typeof b> => Boolean(b))
    .filter((b) => b.status !== 'cancelled' && b.status !== 'no-show' && b.status !== 'superseded');
  const conflictBooking = customerBookings.find((b) => {
    const bt = new Date(b.start).getTime();
    return Math.abs(bt - gapDayStart) <= 3 * DAY_MS;
  });
  const upcomingInWindow = customerBookings.filter((b) => {
    const bt = new Date(b.start).getTime();
    return bt >= gapDayStart && bt <= gapDayStart + 14 * DAY_MS;
  });
  if (conflictBooking) {
    score -= 18;
    evidence.push({ kind: 'calendar-busy', weight: -18, text: 'Already booked within 3 days of this gap' });
  } else if (upcomingInWindow.length === 0) {
    score += 14;
    evidence.push({ kind: 'calendar-open', weight: 14, text: 'Calendar wide open for the next 2 weeks' });
  } else if (upcomingInWindow.length === 1) {
    score += 6;
    evidence.push({ kind: 'calendar-open', weight: 6, text: 'Flexible schedule, one booking in the window' });
  }

  return { score, evidence };
}

/**
 * Candidates to fill one gap, sorted into the five-rung fill ladder (board 08),
 * worked left to right:
 *  1) bring-forward — a future booking with this artist; prefer-brought-forward
 *     to the very top, then fitting blocks, then furthest-out first;
 *  2) deposit — a paid deposit with no date, this artist's first then
 *     unassigned, oldest first;
 *  3) past-client (open business) — prior attended work OR an unfinished piece
 *     with this artist (incl. no-rebook mid-piece), by match score;
 *  4) expired — a deposit link that expired / was never paid;
 *  5) no-show — a previous forfeit / no-show (last, worked only as a fallback).
 * Nobody is excluded for being recently tattooed; the deposit + open-business
 * rungs carry a "days since tattoo" flag instead (tv-25a).
 */
function buildGapQueue(args: BuildArgs, filter: GapFilter): QueueItem[] {
  const artist = args.artists[filter.artistId];
  if (!artist) return [];
  const gapDayStart = new Date(filter.dayIso).getTime();
  const now = new Date(args.nowIso).getTime();

  // Index bookings + pieces by customer once so per-customer scoring is O(small)
  // instead of O(total bookings) — the sweeper works thousands of records.
  const bookingsByCustomer = new Map<string, Booking[]>();
  for (const b of Object.values(args.bookings)) {
    const arr = bookingsByCustomer.get(b.customerId);
    if (arr) arr.push(b);
    else bookingsByCustomer.set(b.customerId, [b]);
  }
  const piecesByCustomer = new Map<string, Piece[]>();
  for (const p of Object.values(args.pieces)) {
    const arr = piecesByCustomer.get(p.customerId);
    if (arr) arr.push(p);
    else piecesByCustomer.set(p.customerId, [p]);
  }

  const makeItem = (
    c: CustomerRecord,
    bucket: GapBucket,
    score: number,
    evidence: MatchEvidence[],
    detail: string,
    depositId?: string,
    recentlyTattooedDays?: number,
  ): QueueItem => {
    const rec = recommendChannel(c);
    const topReasons = evidence
      .filter((e) => e.weight > 0)
      .slice(0, 3)
      .map((e) => e.text)
      .join(' · ');
    // Stamp the customer's sequence-clock state so the sweeper can group
    // candidates by First/Second/Third attempt. A candidate with no sequence
    // leaves stage undefined and is treated as First by consumers.
    const seq = args.sequenceState?.[c.id];
    return {
      id: `q-gap-${c.id}`,
      kind: 'gap-candidate',
      band: 'gap',
      customer: c,
      reason: `${score} match`,
      detail,
      recommendedChannel: rec.channel,
      channelReason: rec.reason,
      channelConfidence: rec.confidence,
      priority: score,
      matchScore: score,
      matchReason: topReasons,
      evidence,
      gapBucket: bucket,
      ...(seq?.stage !== undefined ? { stage: seq.stage } : {}),
      ...(seq?.redKind ? { redKind: seq.redKind } : {}),
      // The sequence "lock": mirror the standard queue's cooldown stamp so the
      // Sweeper can flag a candidate still inside its 24h call-sequence cooldown
      // as hands-off (dormant), while the free-again instant stays visible.
      ...(seq?.nextDueAt
        ? { sequenceDueAtIso: seq.nextDueAt, dormant: new Date(seq.nextDueAt).getTime() > now }
        : {}),
      ...(depositId ? { depositId } : {}),
      ...(recentlyTattooedDays !== undefined ? { recentlyTattooedDays } : {}),
      ...(listedOutcome.has(c.id) ? { sweeperOutcome: listedOutcome.get(c.id) } : {}),
    };
  };

  // FIT check for the bring-forward bucket: compare a candidate's future
  // booking length against the gap's largest real free block, grounded the
  // same way GapFillWorkspace.gapTarget is. Non-fitting candidates aren't
  // hidden — they're deprioritised to the bottom of the bucket.
  const gapSchedule = buildDaySchedule({ artist, bookings: args.bookings, dayIso: filter.dayIso, nowIso: args.nowIso });
  const largestGapWindow = [...gapSchedule.freeWindows].sort((a, b) => b.hours - a.hours)[0];
  const gapBlockMin = largestGapWindow ? largestGapWindow.hours * 60 : 0;
  const gapBlockHours = largestGapWindow?.hours ?? 0;

  // B7: who turned THIS gap down (8xat D08-16), and the latest outcome that
  // leaves a client listed, for the row's "called · no answer" mark.
  const gapKey = gapKeyFor(artist.id, filter.dayIso);
  const turnedDown = turnedDownFor(args.sweeperOutcomes, gapKey);
  const listedOutcome = new Map<string, SweeperOutcomeKind>();
  for (const o of Object.values(args.sweeperOutcomes ?? {})) {
    if (o.gapKey === gapKey && o.outcome !== 'turned-down') listedOutcome.set(o.customerId, o.outcome);
  }

  const bringForward: Array<{ item: QueueItem; bookingMs: number; fits: boolean; prefer: boolean }> = [];
  const deposits: Array<{ item: QueueItem; thisArtist: boolean; depositMs: number }> = [];
  const pastClients: QueueItem[] = [];
  const expiredLinks: QueueItem[] = [];
  const noShows: QueueItem[] = [];

  for (const c of Object.values(args.customers)) {
    if (c.doNotContact) continue;
    if (turnedDown.has(c.id)) continue;

    const custBookings = bookingsByCustomer.get(c.id) ?? [];
    const custPieces = piecesByCustomer.get(c.id) ?? [];
    const custDeposits = c.deposits
      .map((id) => args.deposits[id])
      .filter((d): d is NonNullable<typeof d> => Boolean(d));

    // tv-25a: the healing exclusion is gone. Instead compute days since the
    // client's most recent completed tattoo, surfaced as a flag on the deposit +
    // open-business rungs only (no cut-off — the sweeper decides case by case).
    const lastCompletedMs = custBookings
      .filter((b) => b.status === 'completed')
      .reduce((max, b) => Math.max(max, new Date(b.start).getTime()), -Infinity);
    const tattooDays =
      lastCompletedMs > -Infinity ? Math.max(0, Math.round((now - lastCompletedMs) / DAY_MS)) : undefined;

    const { score, evidence } = gapBaseSignals(c, artist, args, gapDayStart, custBookings, custPieces);

    // Rung 1 — booked ahead with this artist. Ordered by booking date later.
    const futureWithArtist = custBookings
      .filter(
        (b) =>
          b.artistId === artist.id &&
          b.status !== 'cancelled' &&
          b.status !== 'no-show' &&
          b.status !== 'superseded',
      )
      // Strictly a later day than the gap — a booking already on the gap day
      // (e.g. one just moved in) is no longer something to bring forward.
      .filter((b) => new Date(b.start).getTime() >= gapDayStart + DAY_MS)
      .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
    if (futureWithArtist.length > 0) {
      const ahead = futureWithArtist[0]!;
      const fits = ahead.durationMin <= gapBlockMin;
      const prefer = ahead.preferBroughtForward === true;
      const fitEvidence: MatchEvidence = fits
        ? { kind: 'calendar-open', weight: 8, text: `Booked ${ahead.sessionLength} fits this ${gapBlockHours}h gap` }
        : { kind: 'calendar-busy', weight: 0, text: `Booked ${ahead.sessionLength} won't fit this ${gapBlockHours}h gap — would need to split` };
      const preferEvidence: MatchEvidence[] = prefer
        ? [{ kind: 'future-signal', weight: 15, text: 'Asked to be brought forward if a slot opens' }]
        : [];
      const bfItem = makeItem(
        c,
        'bring-forward',
        score,
        [...evidence, fitEvidence, ...preferEvidence],
        `${prefer ? 'Wants to be brought forward · ' : ''}Booked ${gapShortDate(ahead.start)}, could jump the line`,
      );
      // Surface the flag + booking date on the item so the Sweeper row can badge
      // a prefer-brought-forward candidate and order the non-preferred remainder
      // (tv-dme). The prefer-first sort itself stays automatic (see below).
      if (prefer) bfItem.preferBroughtForward = true;
      bfItem.bringForwardBookingIso = ahead.start;
      bfItem.fitsGap = fits;
      bringForward.push({
        item: bfItem,
        bookingMs: new Date(ahead.start).getTime(),
        fits,
        prefer,
      });
      continue;
    }

    // Already booked (75nh D08-14; Anas 1 Oct Q1): a client with any live
    // booking or live hold, with ANY artist, from now on, is only offered a
    // bring-forward of their own booking (rung 1, above) — never rungs 2-5.
    if (custBookings.some((b) => isLiveUpcomingSlot(b, custDeposits, args.nowIso))) continue;

    // Rung 2 — paid deposit on file, no date locked in. Only one that fits the
    // gap (Anas Q2): this artist's deposit, or one for the gap's studio (all
    // artists there are equal) — never another studio's.
    const fitsStudio = (d: Deposit) =>
      d.artistId === artist.id || (d.studioId ?? c.preferredStudioId ?? c.primaryStudioId) === artist.studioId;
    const openDeposits = custDeposits.filter((d) => d.status === 'paid' && !d.bookingId && fitsStudio(d));
    if (openDeposits.length > 0) {
      const depMs = (d: Deposit) => new Date(d.paidAt ?? d.createdAt).getTime();
      const dep = [...openDeposits].sort((a, b) => depMs(a) - depMs(b))[0]!;
      const ageDays = Math.max(0, Math.round((now - depMs(dep)) / DAY_MS));
      const thisArtist = dep.artistId === artist.id;
      const w = Math.min(30, ageDays);
      const depEvidence: MatchEvidence[] = [
        ...evidence,
        {
          kind: 'deposit-age',
          weight: w,
          text: `Deposit ${ageDays}d old${thisArtist ? ` with ${artist.firstName}` : ' (unassigned)'} — convert it`,
        },
      ];
      deposits.push({
        item: makeItem(c, 'deposit', score + w, depEvidence, `${depositAmountLabel(dep)} deposit, ${ageDays}d old, no date set`, dep.id, tattooDays),
        thisArtist,
        depositMs: depMs(dep),
      });
      continue;
    }

    // Rung 3 — open / unfinished business: prior attended work with this artist,
    // OR an unfinished piece assigned to them (catches no-rebook mid-piece).
    const hasPriorWorkWithArtist = custBookings.some(
      (b) =>
        b.artistId === artist.id &&
        b.status !== 'cancelled' &&
        b.status !== 'no-show' &&
        b.status !== 'superseded',
    );
    const hasOpenPieceWithArtist = custPieces.some(
      (p) =>
        p.assignedArtistId === artist.id &&
        (p.status === 'in-progress' || p.status === 'paused-will-return' || p.status === 'planning'),
    );
    if (hasPriorWorkWithArtist || hasOpenPieceWithArtist) {
      let cScore = score;
      const cEvidence = [...evidence];
      const lastMs = lastContactMs(c);
      if (lastMs !== null) {
        const days = Math.max(0, Math.round((now - lastMs) / DAY_MS));
        if (days >= 7) {
          const w = Math.min(20, Math.round(days / 7) * 4);
          cScore += w;
          cEvidence.push({ kind: 'days-since-contact', weight: w, text: `${days}d since last contact — cold, worth a nudge` });
        }
      }
      const stage = args.sequenceState?.[c.id]?.stage;
      if (typeof stage === 'number') {
        const w = (4 - stage) * 3;
        if (w > 0) {
          cScore += w;
          cEvidence.push({ kind: 'sequence', weight: w, text: `Sequence stage ${stage} — earlier stage, not yet chased hard` });
        }
      }
      const topReasons = cEvidence.filter((e) => e.weight > 0).slice(0, 3).map((e) => e.text).join(' · ');
      // 75mu: an unfinished piece carries one deposit pot across its sessions.
      const openPiece = custPieces.find(
        (p) =>
          p.assignedArtistId === artist.id &&
          (p.status === 'in-progress' || p.status === 'paused-will-return' || p.status === 'planning'),
      );
      const pot = openPiece ? piecePotFor(openPiece.id, args.deposits, args.bookings) : 0;
      const potLine = pot > 0 ? `$${pot.toLocaleString('en-AU')} deposit pot on the piece` : '';
      pastClients.push(
        makeItem(
          c,
          'past-client',
          cScore,
          cEvidence,
          [potLine, topReasons].filter(Boolean).join(' · ') || 'Open business',
          undefined,
          tattooDays,
        ),
      );
      continue;
    }

    // Rung 4 — expired links & never-paid: a deposit link that expired.
    // Self-book (artist-link) expired deposits are EXCLUDED (tv-311): an expired
    // self-book link is the Agent's follow-up as a hot lead, not a Sweeper gap
    // item (reconcileSelfBookLinkExpiry routes them). Regular expired deposit
    // links keep their place here.
    // A Sweeper's own lapsed gap-fill link still open on Missed deposits is
    // already on the morning list, so not here too (diagram 08) until it closes.
    const onMissedDeposits = (d: Deposit) =>
      missedLinkOwner(linkSenderRole(d, args.bookings)) === 'sweeper' && !isMissedLinkClosed(d, args.bookings);
    const hasExpiredLink = custDeposits.some(
      (d) => d.status === 'expired' && d.source !== 'artist-link' && !onMissedDeposits(d),
    );
    if (hasExpiredLink) {
      expiredLinks.push(makeItem(c, 'expired', score, evidence, 'Deposit link expired, never paid — worth another try'));
      continue;
    }

    // Rung 5 — forfeits & no-shows (last, worked only when nothing above fills).
    // A forfeit counts whether it came from a no-show or an inside-7-day cancel
    // (sweeper[10], W5b): the cancel leaves the booking 'cancelled', so reading
    // no-show bookings alone missed it. A reinstated forfeit is not one.
    const hasNoShow = custBookings.some((b) => b.status === 'no-show');
    const forfeit = c.deposits
      .map((id) => args.deposits[id])
      .find((d): d is Deposit => !!d && isDepositForfeited(d));
    if (hasNoShow) {
      noShows.push(makeItem(c, 'no-show', score, evidence, 'Previous no-show / forfeit'));
    } else if (forfeit) {
      noShows.push(
        makeItem(c, 'no-show', score, evidence, `$${forfeitedAud(forfeit)} forfeited on a late cancel — worth another try`),
      );
    }
  }

  bringForward.sort((a, b) => {
    if (a.prefer !== b.prefer) return a.prefer ? -1 : 1; // prefer-brought-forward to the very top
    if (a.fits !== b.fits) return a.fits ? -1 : 1; // fitting block next
    return b.bookingMs - a.bookingMs; // furthest-out first within each group
  });
  deposits.sort((a, b) => {
    if (a.thisArtist !== b.thisArtist) return a.thisArtist ? -1 : 1;
    return a.depositMs - b.depositMs; // oldest first
  });
  pastClients.sort((a, b) => b.priority - a.priority);
  expiredLinks.sort((a, b) => b.priority - a.priority);
  noShows.sort((a, b) => b.priority - a.priority);

  return [
    ...bringForward.map((x) => x.item),
    ...deposits.map((x) => x.item),
    ...pastClients,
    ...expiredLinks,
    ...noShows,
  ];
}

function castChannel(c: string): QueueChannel {
  if (c === 'voice' || c === 'sms' || c === 'email' || c === 'meta-dm' || c === 'ig-dm') return c;
  return 'sms';
}

function labelChannel(c: QueueChannel): string {
  if (c === 'voice') return 'voice';
  if (c === 'sms') return 'SMS';
  if (c === 'meta-dm') return 'Messenger';
  if (c === 'ig-dm') return 'IG DM';
  return 'email';
}

function lastMatching<T>(arr: T[], pred: (x: T) => boolean): T | undefined {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (pred(arr[i]!)) return arr[i];
  }
  return undefined;
}

function formatHours(ms: number): string {
  if (ms < HOUR) return `${Math.max(1, Math.round(ms / 60_000))}m`;
  if (ms < 48 * HOUR) return `${Math.round(ms / HOUR)}h`;
  return `${Math.round(ms / (24 * HOUR))}d`;
}

// Common context bullets appended to every standard queue item so the "Why
// this matters" panel has substance for all kinds, not just gap candidates.
function contextEvidence(
  customer: CustomerRecord,
  primary: MatchEvidence[],
): MatchEvidence[] {
  const out: MatchEvidence[] = [...primary];
  if (customer.lifetimeSpendAud >= 5000) {
    out.push({
      kind: 'spend-tier',
      weight: 20,
      text: `VIP: $${customer.lifetimeSpendAud.toLocaleString()} lifetime spend`,
    });
  } else if (customer.lifetimeSpendAud >= 2000) {
    out.push({
      kind: 'spend-tier',
      weight: 12,
      text: `High value: $${customer.lifetimeSpendAud.toLocaleString()} lifetime`,
    });
  } else if (customer.lifetimeSpendAud >= 500) {
    out.push({
      kind: 'spend-tier',
      weight: 6,
      text: `Returning: $${customer.lifetimeSpendAud.toLocaleString()} lifetime`,
    });
  } else if (customer.lifetimeSpendAud === 0) {
    out.push({
      kind: 'spend-tier',
      weight: 4,
      text: 'Brand-new lead, no prior spend',
    });
  }
  if (customer.distanceToNearestStudioKm > 0) {
    out.push({
      kind: 'proximity',
      weight: customer.distanceToNearestStudioKm <= 5 ? 6 : 2,
      text: `${customer.distanceToNearestStudioKm.toFixed(1)} km from nearest studio · ${customer.suburb}`,
    });
  }
  if (customer.engagementStatus === 'lapsed') {
    out.push({
      kind: 'engagement',
      weight: 5,
      text: 'Lapsed, hasn\'t been in for a while',
    });
  } else if (customer.engagementStatus === 'dormant') {
    out.push({
      kind: 'engagement',
      weight: 8,
      text: 'Dormant, long overdue',
    });
  }
  return out;
}

export interface HeatmapCell {
  artistId: string;
  dayIso: string;
  dayOfWeek: DayOfWeek;
  freeHours: number;
  workingHours: number;
}

export interface HeatmapRow {
  artist: Artist;
  cells: HeatmapCell[];
}

const DOW: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function buildHeatmap(args: {
  artists: Record<string, Artist>;
  bookings: Record<string, Booking>;
  nowIso: string;
  studioIds: StudioId[];
  /** Window start (day-normalised). Defaults to nowIso so existing callers are unchanged. */
  startIso?: string;
  days?: number;
  /**
   * L5 (q44j #3): include apprentices. The Floor's walk-in grid books same-day
   * apprentice walk-ins (D10-18); every other grid keeps them out (default).
   */
  includeApprentices?: boolean;
}): HeatmapRow[] {
  const days = args.days ?? 14;
  const allow = new Set(args.studioIds);
  const inStudio = (id: StudioId) => allow.size === 0 || allow.has(id);
  const start = new Date(args.startIso ?? args.nowIso);
  start.setHours(0, 0, 0, 0);
  const nowMs = new Date(args.nowIso).getTime();

  const artistList = Object.values(args.artists)
    .filter((a) => inStudio(a.studioId))
    .filter((a) => !a.archived && (args.includeApprentices === true || !a.isApprentice))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return artistList.map((artist) => {
    const cells: HeatmapCell[] = [];
    for (let i = 0; i < days; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      const dow = DOW[day.getDay()]!;
      const window = artist.availability.pattern[dow];
      if (!window) {
        cells.push({ artistId: artist.id, dayIso: day.toISOString(), dayOfWeek: dow, freeHours: 0, workingHours: 0 });
        continue;
      }
      const winStart = parseTime(window.startTime, day);
      const winEnd = parseTime(window.endTime, day);
      const workingHours = Math.max(0, (winEnd - winStart) / HOUR);
      const dayStart = day.getTime();
      const dayEnd = dayStart + 24 * HOUR;
      const dayBookings = Object.values(args.bookings)
        .filter((b) => b.artistId === artist.id)
        .filter((b) => {
          if (b.status === 'cancelled' || b.status === 'no-show' || b.status === 'superseded')
            return false;
          const t = new Date(b.start).getTime();
          return t >= dayStart && t < dayEnd;
        });
      // Free time reflects the REAL calendar: the roster window minus actual
      // (non-cancelled) bookings. An open day with no bookings reads as fully
      // free, matching the calendar. Hours already past on the sim clock are not
      // free (tv-2na.10.3): a past day is 0, and today counts only the roster
      // left after now, less the booked time still ahead of now (as buildGapList).
      let freeHours: number;
      if (nowMs >= winEnd) {
        freeHours = 0;
      } else if (nowMs > winStart) {
        const bookedAhead = dayBookings.reduce((sum, b) => {
          const bStart = new Date(b.start).getTime();
          const bEnd = bStart + b.durationMin * 60 * 1000;
          return sum + Math.max(0, Math.min(bEnd, winEnd) - Math.max(bStart, nowMs)) / HOUR;
        }, 0);
        freeHours = Math.max(0, Math.round((winEnd - nowMs) / HOUR - bookedAhead));
      } else {
        const realUsed = dayBookings.reduce((sum, b) => sum + b.durationMin / 60, 0);
        freeHours = Math.max(0, Math.round(workingHours - realUsed));
      }
      cells.push({
        artistId: artist.id,
        dayIso: day.toISOString(),
        dayOfWeek: dow,
        freeHours,
        workingHours,
      });
    }
    return { artist, cells };
  });
}

export type GapWeekWindow = 'current' | 'next';
export type GapUrgency = 'today' | 'tomorrow' | 'week';

export interface GapListRow {
  id: string;
  artist: Artist;
  studioId: StudioId;
  dayIso: string;
  /** Total open hours that day, fragmented windows summed into one figure. */
  openHours: number;
  /** Rostered hours that day, from availability.pattern. */
  rosteredHours: number;
  /** Tier 1 = near-full (>=90% rostered) or 4h+; Tier 2 = smaller part days. */
  tier: 1 | 2;
  urgency: GapUrgency;
}

// Below this many open hours a day isn't worth surfacing as a gap. Tunable.
const MIN_GAP_HOURS = 2;
// How many weeks forward the "next week onwards" window spans. Tunable.
const NEXT_WINDOW_WEEKS = 4;

function startOfLocalDay(ms: number): Date {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Window bounds for a gap surface. 'current' = today→end of this week (Mon–Sun);
 * 'next' = from next Monday spanning NEXT_WINDOW_WEEKS weeks. */
function gapWindow(nowIso: string, weekWindow: GapWeekWindow): { start: Date; days: number } {
  const today = startOfLocalDay(new Date(nowIso).getTime());
  const dow = today.getDay(); // 0 Sun .. 6 Sat
  if (weekWindow === 'current') {
    const daysLeft = dow === 0 ? 1 : 8 - dow;
    return { start: today, days: daysLeft };
  }
  const rawToMonday = (1 - dow + 7) % 7;
  const toNextMonday = rawToMonday === 0 ? 7 : rawToMonday;
  const start = new Date(today);
  start.setDate(today.getDate() + toNextMonday);
  return { start, days: NEXT_WINDOW_WEEKS * 7 };
}

/**
 * Ordered list of open-hour gaps for a set of studios, one row per artist/day,
 * fragmented windows summed. Built from availability minus REAL bookings (not
 * the heatmap's synthetic utilisation) so every row is a genuinely fillable
 * slab. Ordering is opportunity-size first, tempered by urgency: Tier 1 rows
 * (near-full or 4h+) soonest-first sit above all Tier 2 rows (part days), which
 * sort by open hours descending then soonest. One flat list across all studios.
 */
export function buildGapList(args: {
  artists: Record<string, Artist>;
  bookings: Record<string, Booking>;
  nowIso: string;
  studioIds: StudioId[];
  weekWindow: GapWeekWindow;
}): GapListRow[] {
  const allow = new Set(args.studioIds);
  const inStudio = (id: StudioId) => allow.size === 0 || allow.has(id);
  const { start, days } = gapWindow(args.nowIso, args.weekWindow);
  const nowMs = new Date(args.nowIso).getTime();
  const todayYmd = ymd(startOfLocalDay(nowMs));
  const tomorrow = startOfLocalDay(nowMs);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowYmd = ymd(tomorrow);

  const artistList = Object.values(args.artists)
    .filter((a) => inStudio(a.studioId))
    .filter((a) => !a.isApprentice && !a.archived);

  // Index live bookings by artist once so each cell scans a small per-artist
  // list, not the whole booking table.
  const bookingsByArtist = new Map<string, Booking[]>();
  for (const b of Object.values(args.bookings)) {
    if (b.status === 'cancelled' || b.status === 'no-show' || b.status === 'superseded') continue;
    const arr = bookingsByArtist.get(b.artistId);
    if (arr) arr.push(b);
    else bookingsByArtist.set(b.artistId, [b]);
  }

  const rows: GapListRow[] = [];
  for (const artist of artistList) {
    for (let i = 0; i < days; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      const dayYmd = ymd(day);
      const dow = DOW[day.getDay()]!;
      const window = artist.availability.pattern[dow];
      if (!window) continue;
      // A day off by studio day key (8uf3): a calendar-made block is a UTC instant
      // of studio midnight, a seeded one `+10:00`; the raw string compare missed both.
      if (resolveDayOff(artist.availability, day) !== null) continue;

      const winStart = parseTime(window.startTime, day);
      const winEnd = parseTime(window.endTime, day);
      const rosteredHours = Math.max(0, (winEnd - winStart) / HOUR);
      if (rosteredHours <= 0) continue;

      const dayStart = day.getTime();
      const dayEnd = dayStart + 24 * HOUR;
      const dayBookings = (bookingsByArtist.get(artist.id) ?? []).filter((b) => {
        const t = new Date(b.start).getTime();
        return t >= dayStart && t < dayEnd;
      });

      // Open hours come from REAL bookings (fragmented windows summed), so filling
      // or vacating a slot moves the number immediately, matching the calendar.
      let openHours: number;
      if (dayYmd === todayYmd && nowMs > winStart) {
        // Today (tv-2na.3): hours already past can't be filled. Only the roster
        // left after now counts, less the booked time still ahead of now.
        if (nowMs >= winEnd) continue;
        const bookedAhead = dayBookings.reduce((sum, b) => {
          const bStart = new Date(b.start).getTime();
          const bEnd = bStart + b.durationMin * 60 * 1000;
          return sum + Math.max(0, Math.min(bEnd, winEnd) - Math.max(bStart, nowMs)) / HOUR;
        }, 0);
        openHours = Math.max(0, Math.round((winEnd - nowMs) / HOUR - bookedAhead));
      } else {
        const realUsed = dayBookings.reduce((sum, b) => sum + b.durationMin / 60, 0);
        openHours = Math.max(0, Math.round(rosteredHours - realUsed));
      }

      // Surface any day with a real gap worth filling — the sweeper reflects the
      // actual calendar, not a synthetic busy-studio model.
      if (openHours < MIN_GAP_HOURS) continue;

      const tier: 1 | 2 = openHours / rosteredHours >= 0.9 || openHours >= 4 ? 1 : 2;
      const urgency: GapUrgency =
        dayYmd === todayYmd ? 'today' : dayYmd === tomorrowYmd ? 'tomorrow' : 'week';

      rows.push({
        id: `${artist.id}-${dayYmd}`,
        artist,
        studioId: artist.studioId,
        dayIso: day.toISOString(),
        openHours,
        rosteredHours: Math.round(rosteredHours),
        tier,
        urgency,
      });
    }
  }

  rows.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier; // Tier 1 above Tier 2
    if (a.tier === 1) return new Date(a.dayIso).getTime() - new Date(b.dayIso).getTime();
    if (a.openHours !== b.openHours) return b.openHours - a.openHours; // Tier 2: hours desc
    return new Date(a.dayIso).getTime() - new Date(b.dayIso).getTime(); // then soonest
  });

  return rows;
}

/**
 * Agent vs Sweeper gap division (board 08 + 05, decided 18 Aug): the Sweeper
 * takes artists with only 1-2 gaps; artists with 3+ gaps (big clumps) are the
 * Agent's. A "gap" is one buildGapList row (an artist-day with 2h+ open).
 */
export const SWEEPER_MAX_GAPS_PER_ARTIST = 2;

/** Gap rows per artist over the rows given (one row = one artist-day gap). */
function gapsPerArtist(rows: GapListRow[]): Map<string, number> {
  const perArtist = new Map<string, number>();
  for (const r of rows) perArtist.set(r.artist.id, (perArtist.get(r.artist.id) ?? 0) + 1);
  return perArtist;
}

export interface SweeperGapList {
  /** Which week the rail is working: this week first, next week once it's clear. */
  week: 'this' | 'next';
  rows: GapListRow[];
}

/**
 * The Sweeper's gap rail (tv-2na.3). The gap count is taken per artist over
 * the single week being swept: THIS week (today from now, past hours trimmed,
 * through Sunday) — or, when this week has no 1-2-gap artists left, NEXT week
 * (next Monday through Sunday). Artists with 3+ gaps in that week are left out.
 */
export function buildSweeperGapList(args: {
  artists: Record<string, Artist>;
  bookings: Record<string, Booking>;
  nowIso: string;
  studioIds: StudioId[];
}): SweeperGapList {
  const smallGapArtistsOnly = (rows: GapListRow[]): GapListRow[] => {
    const perArtist = gapsPerArtist(rows);
    return rows.filter((r) => (perArtist.get(r.artist.id) ?? 0) <= SWEEPER_MAX_GAPS_PER_ARTIST);
  };

  const thisWeek = smallGapArtistsOnly(buildGapList({ ...args, weekWindow: 'current' }));
  if (thisWeek.length > 0) return { week: 'this', rows: thisWeek };

  // This week is clear: next week's small-gap artists (one week, not the
  // Agent's multi-week "next" horizon).
  const { start } = gapWindow(args.nowIso, 'next');
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  const nextWeek = buildGapList({ ...args, weekWindow: 'next' }).filter(
    (r) => new Date(r.dayIso).getTime() < end.getTime(),
  );
  return { week: 'next', rows: smallGapArtistsOnly(nextWeek) };
}

/**
 * The Agent's gap view (tv-2na.13.14, boards 05 + 08, option A decided 23 Sep):
 * THIS week only (today from now, past hours trimmed, through Sunday), kept to
 * artists with 3+ gaps in it, the inverse of the Sweeper's 1-2 gap filter. No
 * next-week fallback: an artist is on one lane or the other for this week.
 */
export function buildAgentGapList(args: {
  artists: Record<string, Artist>;
  bookings: Record<string, Booking>;
  nowIso: string;
  studioIds: StudioId[];
}): GapListRow[] {
  const rows = buildGapList({ ...args, weekWindow: 'current' });
  const perArtist = gapsPerArtist(rows);
  return rows.filter((r) => (perArtist.get(r.artist.id) ?? 0) > SWEEPER_MAX_GAPS_PER_ARTIST);
}

function parseTime(hhmm: string, baseDate: Date): number {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  const d = new Date(baseDate);
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d.getTime();
}

export interface DaySchedule {
  artistId: string;
  dayIso: string;
  working: { startHour: number; endHour: number } | null;
  bookings: Array<{
    id: string;
    startHour: number;
    endHour: number;
    customerId: string;
    designBrief: string;
  }>;
  freeWindows: Array<{ startHour: number; endHour: number; hours: number }>;
}

export function buildDaySchedule(args: {
  artist: Artist;
  bookings: Record<string, Booking>;
  dayIso: string;
  /** The sim clock. Hours already past can't be filled (tv-2na.3,
   * tv-2na.10.3): today's free windows start at now and a past day has none.
   * Required so no picker forgets it; pass `null` only for a deliberately
   * unclipped read (a day's shape, not what is still bookable). */
  nowIso: string | null;
}): DaySchedule {
  const day = new Date(args.dayIso);
  day.setHours(0, 0, 0, 0);
  const dow = DOW[day.getDay()]!;
  const window = args.artist.availability.pattern[dow];
  if (!window) {
    return {
      artistId: args.artist.id,
      dayIso: day.toISOString(),
      working: null,
      bookings: [],
      freeWindows: [],
    };
  }

  const startHour = hourOf(window.startTime);
  const endHour = hourOf(window.endTime);
  const dayStart = day.getTime();
  const dayEnd = dayStart + 24 * HOUR;

  const dayBookings = Object.values(args.bookings)
    .filter((b) => b.artistId === args.artist.id)
    .filter((b) => b.status !== 'cancelled' && b.status !== 'no-show' && b.status !== 'superseded')
    .filter((b) => {
      const t = new Date(b.start).getTime();
      return t >= dayStart && t < dayEnd;
    })
    .map((b) => {
      const bStart = new Date(b.start);
      const sh = bStart.getHours() + bStart.getMinutes() / 60;
      return {
        id: b.id,
        startHour: sh,
        endHour: sh + b.durationMin / 60,
        customerId: b.customerId,
        designBrief: b.designBrief,
      };
    })
    .sort((a, b) => a.startHour - b.startHour);

  let freeWindows: DaySchedule['freeWindows'] = [];
  let cursor = startHour;
  for (const b of dayBookings) {
    if (b.startHour > cursor) {
      freeWindows.push({
        startHour: cursor,
        endHour: b.startHour,
        hours: Math.round((b.startHour - cursor) * 10) / 10,
      });
    }
    cursor = Math.max(cursor, b.endHour);
  }
  if (cursor < endHour) {
    freeWindows.push({
      startHour: cursor,
      endHour,
      hours: Math.round((endHour - cursor) * 10) / 10,
    });
  }

  // The day schedule shows the REAL calendar only — no synthetic filler. An open
  // roster with no bookings reads as free, matching the heatmap and the calendar.
  if (args.nowIso) {
    const now = new Date(args.nowIso);
    const nowDay = new Date(now);
    nowDay.setHours(0, 0, 0, 0);
    if (nowDay.getTime() > dayStart) {
      freeWindows = [];
    } else if (nowDay.getTime() === dayStart) {
      const nowHour = now.getHours() + now.getMinutes() / 60;
      freeWindows = freeWindows
        .filter((w) => w.endHour > nowHour)
        .map((w) =>
          w.startHour >= nowHour
            ? w
            : { startHour: nowHour, endHour: w.endHour, hours: Math.round((w.endHour - nowHour) * 10) / 10 },
        );
    }
  }
  freeWindows = freeWindows.filter((w) => w.hours >= 0.5);
  const allBookings = [...dayBookings].sort((a, b) => a.startHour - b.startHour);

  return {
    artistId: args.artist.id,
    dayIso: day.toISOString(),
    working: { startHour, endHour },
    bookings: allBookings,
    freeWindows,
  };
}

function hourOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  return (h ?? 0) + (m ?? 0) / 60;
}
